import React, { useState, useEffect } from 'react';
import { fetchPastWordsOfTheDay, PastWordDay } from '../services/vocabularyService';
import { X, Calendar } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useWord } from '../contexts/WordContext';

interface PastWordsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PastWordsModal({ isOpen, onClose }: PastWordsModalProps) {
  const [words, setWords] = useState<PastWordDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDate, setLastDate] = useState<string | undefined>(undefined);
  const { loadWordByDate } = useWord();

  const PAGE_SIZE = 10;

  useEffect(() => {
    if (isOpen && words.length === 0) {
      loadMore();
    }
  }, [isOpen]);

  const loadMore = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    
    try {
      const newWords = await fetchPastWordsOfTheDay(PAGE_SIZE, lastDate);
      
      if (newWords.length < PAGE_SIZE) {
        setHasMore(false);
      }
      
      if (newWords.length > 0) {
        setWords(prev => [...prev, ...newWords]);
        setLastDate(newWords[newWords.length - 1].date);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching past words:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWordClick = async (date: string) => {
    await loadWordByDate(date);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-purple-50">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Past Words of the Day
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-purple-100 rounded-full transition-colors text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {words.length === 0 && !loading ? (
            <p className="text-center text-gray-500 py-8">No past words found.</p>
          ) : (
            <div className="space-y-3">
              {words.map((word, index) => (
                <div 
                  key={`${word.date}-${index}`} 
                  onClick={() => handleWordClick(word.date)}
                  className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex justify-between items-center hover:border-purple-200 transition-colors cursor-pointer hover:bg-purple-50"
                >
                  <div>
                    <h3 className="font-bold text-lg text-gray-800 capitalize">{word.word}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(word.timestamp).toLocaleDateString(undefined, { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full"></div>
            </div>
          )}

          {!loading && hasMore && words.length > 0 && (
            <button
              onClick={loadMore}
              className="w-full mt-4 py-3 bg-white border-2 border-purple-100 text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-colors"
            >
              Load More
            </button>
          )}

          {!hasMore && words.length > 0 && (
            <p className="text-center text-gray-400 text-sm mt-6">
              You've reached the end of the history.
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
